/**
 * ============================================================================
 * TEMPLATE MANAGER COMPONENT v1.2
 * ============================================================================
 * Modal zur Verwaltung von Termin-Templates
 *
 * Features:
 * - Templates anzeigen (Liste)
 * - Templates erstellen/bearbeiten
 * - Templates löschen
 * - Validierung (Template-Name erforderlich)
 *
 * v1.2: Person-Dropdown mit echten Haushaltsmitgliedern (HouseholdContext)
 * v1.1: Fixed transparent modal background
 *
 * @version 1.2.0 - Phase 1: Quick Wins
 * ============================================================================
 */

import { useState, useMemo } from 'react';
import { getCategoryInfo } from './calendarUtils';
import { useHousehold } from '../../context/HouseholdContext';

// Fallback wenn keine Members geladen
const DEFAULT_MEMBERS = [
  { id: 'alle', value: 'alle', name: 'Alle', avatar: '👥' }
];

// Fixed color constants for consistent styling
const COLORS = {
  bg: '#FFFFFF',
  text: '#2D3748',
  textSecondary: '#718096',
  border: '#E2E8F0',
  hover: '#F7FAFC',
  primary: '#4A90A4',
  danger: '#dc3545'
};

export default function TemplateManager({ templates, onClose, onSave, onDelete }) {
  // Hole Haushaltsmitglieder vom Context
  const { getMemberOptions } = useHousehold();

  // Memoized Member Options
  const PERSON_OPTIONS = useMemo(() => {
    const options = getMemberOptions ? getMemberOptions(true) : [];

    if (!options || options.length === 0) {
      console.warn('⚠️ No members from context, using DEFAULT_MEMBERS');
      return DEFAULT_MEMBERS;
    }

    // Formatiere für Anzeige mit Avatar
    return options.map(opt => ({
      id: opt.value || opt.id,
      name: `${opt.avatar || opt.icon || '👤'} ${opt.name || opt.label}`,
      avatar: opt.avatar || opt.icon || '👤'
    }));
  }, [getMemberOptions]);

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    template_name: '',
    title: '',
    description: '',
    duration_minutes: 60,
    category: 'privat',
    person: 'alle',
    location: '',
    reminder_minutes: null,
    all_day: false
  });

  const resetForm = () => {
    setFormData({
      template_name: '',
      title: '',
      description: '',
      duration_minutes: 60,
      category: 'privat',
      person: 'alle',
      location: '',
      reminder_minutes: null,
      all_day: false
    });
    setEditingTemplate(null);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      template_name: template.template_name || '',
      title: template.title || '',
      description: template.description || '',
      duration_minutes: template.duration_minutes || 60,
      category: template.category || 'privat',
      person: template.person || 'alle',
      location: template.location || '',
      reminder_minutes: template.reminder_minutes,
      all_day: template.all_day || false
    });
  };

  const handleSave = async () => {
    if (!formData.template_name.trim()) {
      alert('Bitte Template-Namen eingeben');
      return;
    }

    const templateData = {
      ...formData,
      reminder_minutes: formData.reminder_minutes || null
    };

    if (editingTemplate) {
      // Update existing template
      await onSave({ ...templateData, id: editingTemplate.id }, true);
    } else {
      // Create new template
      await onSave(templateData, false);
    }

    resetForm();
  };

  const handleDelete = async (templateId) => {
    if (window.confirm('Möchten Sie dieses Template wirklich löschen?')) {
      await onDelete(templateId);
      if (editingTemplate?.id === templateId) {
        resetForm();
      }
    }
  };

  // Common input style
  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${COLORS.border}`,
    borderRadius: '8px',
    backgroundColor: COLORS.bg,
    color: COLORS.text,
    fontSize: '0.95rem'
  };

  // Common label style
  const labelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: '4px'
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: COLORS.bg,
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '900px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          border: `2px solid ${COLORS.border}`
        }}
      >
        <button
          onClick={onClose}
          className="close-modal"
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: COLORS.textSecondary,
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = COLORS.hover}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          ✕
        </button>

        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: COLORS.text,
          marginBottom: '24px'
        }}>
          ⚙️ Templates verwalten
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Left: Template List */}
          <div>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: COLORS.text,
              marginBottom: '12px'
            }}>
              Vorhandene Templates
            </h3>

            {templates.length === 0 ? (
              <p style={{
                color: COLORS.textSecondary,
                fontSize: '0.9rem',
                fontStyle: 'italic',
                padding: '20px',
                textAlign: 'center',
                background: COLORS.hover,
                borderRadius: '8px'
              }}>
                Noch keine Templates vorhanden
              </p>
            ) : (
              <div className="template-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {templates.map(template => {
                  const categoryInfo = getCategoryInfo(template.category);
                  const isEditing = editingTemplate?.id === template.id;

                  return (
                    <div
                      key={template.id}
                      className="template-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        background: isEditing ? 'rgba(74, 144, 164, 0.15)' : COLORS.hover,
                        borderRadius: '8px',
                        border: isEditing ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: '600',
                          color: COLORS.text,
                          fontSize: '0.95rem',
                          marginBottom: '4px'
                        }}>
                          {template.template_name}
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: COLORS.textSecondary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>{categoryInfo.icon} {categoryInfo.label}</span>
                          {template.duration_minutes && (
                            <span>• {template.duration_minutes} Min.</span>
                          )}
                        </div>
                      </div>

                      <div className="template-actions" style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleEdit(template)}
                          style={{
                            padding: '6px 10px',
                            background: 'transparent',
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = COLORS.primary;
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.borderColor = COLORS.primary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'inherit';
                            e.currentTarget.style.borderColor = COLORS.border;
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          style={{
                            padding: '6px 10px',
                            background: 'transparent',
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = COLORS.danger;
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.borderColor = COLORS.danger;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'inherit';
                            e.currentTarget.style.borderColor = COLORS.border;
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: Create/Edit Form */}
          <div>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: COLORS.text,
              marginBottom: '12px'
            }}>
              {editingTemplate ? '✏️ Template bearbeiten' : '➕ Neues Template'}
            </h3>

            <div className="template-form" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Template-Name *</label>
                <input
                  type="text"
                  placeholder="z.B. 'Arzttermin', 'Team Meeting'"
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Titel</label>
                <input
                  type="text"
                  placeholder="Standard-Titel für Termine"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Beschreibung</label>
                <textarea
                  placeholder="Standard-Beschreibung"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Kategorie</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      ...inputStyle,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="privat">🏠 Privat</option>
                    <option value="arzt">⚕️ Arzt</option>
                    <option value="schule">🎓 Schule</option>
                    <option value="arbeit">💼 Arbeit</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Person</label>
                  <select
                    value={formData.person}
                    onChange={(e) => setFormData({ ...formData, person: e.target.value })}
                    style={{
                      ...inputStyle,
                      cursor: 'pointer'
                    }}
                  >
                    {PERSON_OPTIONS.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Ort</label>
                <input
                  type="text"
                  placeholder="Standard-Ort"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Dauer (Min.)</label>
                  <input
                    type="number"
                    min="1"
                    step="15"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Erinnerung (Min.)</label>
                  <input
                    type="number"
                    min="0"
                    step="5"
                    placeholder="Keine"
                    value={formData.reminder_minutes || ''}
                    onChange={(e) => setFormData({ ...formData, reminder_minutes: e.target.value ? parseInt(e.target.value) : null })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.9rem',
                  color: COLORS.text,
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.all_day}
                    onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                    style={{ cursor: 'pointer' }}
                  />
                  Ganztägig
                </label>
              </div>

              <div className="form-actions" style={{
                display: 'flex',
                gap: '10px',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${COLORS.border}`
              }}>
                <button
                  onClick={handleSave}
                  className="save-btn"
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 144, 164, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  💾 {editingTemplate ? 'Aktualisieren' : 'Speichern'}
                </button>

                {editingTemplate && (
                  <button
                    onClick={resetForm}
                    className="cancel-btn"
                    style={{
                      padding: '10px 16px',
                      background: 'transparent',
                      color: COLORS.textSecondary,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = COLORS.hover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
